'use client'

import { cn } from '@brew/ui/cn'
import { useTranslations } from 'next-intl'
import {
  type CSSProperties,
  type KeyboardEvent,
  useCallback,
  useRef,
  useState,
} from 'react'
import { TemplateBrandLogo } from '@/app/(app)/templates/components/template-brand-logo'
import { EmailFrame } from '@/components/email-detail/email-frame'
import { EmailDesignPreview } from '@/components/email-preview'
import { useAppLocale } from '@/components/i18n/locale-provider'
import { describeStepGap } from '@/lib/flow-templates/format-flow-timing'
import { numberFormat } from '@/lib/i18n/format'
import { createRafResizeObserver } from '@/lib/shared/dom/raf-resize-observer'
import {
  FlowChevronLeftIcon,
  FlowChevronRightIcon,
  FlowClockIcon,
} from './flow-template-icons'

/**
 * The flow page's stage — the template page's framed email, extended to a
 * sequence. The email in focus sits full size in the centre, whole and
 * uncropped, with its subject and preview text above it; the previous and
 * next emails wait scaled down at either side under "2 days before" /
 * "2 days later", and a click (or ←/→) glides one of them into focus.
 * Only the focus and its two neighbours are mounted. One email is simply
 * the template page's view. No effects: the measurement lives in a ref
 * callback.
 *
 * All three are measured, not just the focus: a short email in focus must
 * still leave the long ones beside it their full height rather than crop
 * them to its own.
 */

export interface StageBrand {
  domain: string
  name: string
  logoUrl?: string
}

export interface StageEmail {
  /** Stable identity across focus changes (the step's public reference). */
  key: string
  subject: string
  previewText?: string
  dayOffset: number
  categoryLabel: string
  jsx: string
  html: string | null
  /** The column width the email renders at (its own width plus the frame). */
  width: number
}

/**
 * A neighbouring email sits smaller and duller beside the focus, its inner
 * edge tucked under the focus frame — a deck, not a row. The tuck is a share
 * of the focus column width.
 */
const SIDE_SCALE = 0.5
const SIDE_TUCK_PERCENT = 12
/** Where a neighbour's centre lands, as a share of the focus column width. */
const SIDE_SHIFT_PERCENT = 50 + SIDE_SCALE * 50 - SIDE_TUCK_PERCENT
/** A neighbour starts this far below the focus frame's top. */
const SIDE_TOP_OFFSET_PX = 96
const STAGE_MIN_HEIGHT_PX = 480
const MOTION_CLASSES =
  'motion-safe:transition-[transform,opacity,filter,max-width,height] motion-safe:duration-500 motion-safe:ease-[cubic-bezier(0.32,0.72,0,1)]'

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false
}

type StageSlot = 'focus' | 'previous' | 'next'

function slotFor(offset: number): StageSlot {
  if (offset < 0) {
    return 'previous'
  }
  return offset > 0 ? 'next' : 'focus'
}

function sideTransform(direction: -1 | 1): string {
  return `translateX(${direction * SIDE_SHIFT_PERCENT}%) translateY(${SIDE_TOP_OFFSET_PX}px) scale(${SIDE_SCALE})`
}

function sideLabelLeft(direction: -1 | 1): string {
  return `${50 + direction * SIDE_SHIFT_PERCENT}%`
}

function PreviewUnavailable() {
  const t = useTranslations('browse')
  return (
    <div className="flex h-[480px] items-center justify-center text-muted-foreground text-sm">
      {t('flows.stage.previewUnavailable')}
    </div>
  )
}

function StageEmailFrame({ email }: { email: StageEmail }) {
  return (
    <EmailFrame>
      <EmailDesignPreview
        jsx={email.jsx}
        html={email.html}
        title={email.subject}
        fitToWidth
        fallback={<PreviewUnavailable />}
        brokenState={<PreviewUnavailable />}
        unmeasuredMinHeight={STAGE_MIN_HEIGHT_PX}
      />
    </EmailFrame>
  )
}

/** "2 days before" / "2 days later" over a neighbour; clicking it brings that email in. */
function SideLabel({
  direction,
  label,
  onClick,
}: {
  direction: -1 | 1
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-hidden
      tabIndex={-1}
      className="motion-safe:fade-in absolute top-0 hidden -translate-x-1/2 -translate-y-8 items-center gap-1.5 whitespace-nowrap font-medium text-muted-foreground text-xs transition-colors hover:text-foreground motion-safe:animate-in motion-safe:duration-500 md:flex"
      style={{ left: sideLabelLeft(direction), top: SIDE_TOP_OFFSET_PX }}
    >
      <FlowClockIcon aria-hidden className="size-3.5" />
      {label}
    </button>
  )
}

function FocusHeader({
  email,
  brand,
}: {
  email: StageEmail
  brand?: StageBrand
}) {
  return (
    <div
      key={email.key}
      className="motion-safe:fade-in flex items-start gap-4 motion-safe:animate-in motion-safe:duration-300"
    >
      <div className="flex min-w-0 items-start gap-3">
        {brand ? (
          <TemplateBrandLogo
            domain={brand.domain}
            name={brand.name}
            logoUrl={brand.logoUrl}
            className="mt-0.5 size-9 shrink-0 rounded-lg"
          />
        ) : null}
        <div className="flex min-w-0 flex-col gap-0.5">
          <h2 className="font-medium text-base text-foreground leading-snug md:text-lg">
            {email.subject}
          </h2>
          {email.previewText ? (
            <p className="line-clamp-2 text-foreground/70 text-sm leading-snug">
              {email.previewText}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export function FlowEmailStage({
  emails,
  brand,
}: {
  emails: ReadonlyArray<StageEmail>
  /** Whose emails these are — the logo sits beside the focus subject. */
  brand?: StageBrand
}) {
  const t = useTranslations('browse')
  const tFlows = useTranslations('flows')
  const locale = useAppLocale()
  const count = emails.length
  const [focusIndex, setFocusIndex] = useState(0)
  /** Unscaled height of every mounted email, by its stable step key. */
  const [heightByKey, setHeightByKey] = useState<Record<string, number>>({})
  const focus = emails[focusIndex]
  const previous = focusIndex > 0 ? emails[focusIndex - 1] : undefined
  const next = focusIndex < count - 1 ? emails[focusIndex + 1] : undefined
  const sectionRef = useRef<HTMLElement | null>(null)

  const goTo = useCallback(
    (index: number) => {
      setFocusIndex(Math.max(0, Math.min(count - 1, index)))
      // A click deep down a long email brings the next one in; glide back
      // up so it is read from its header, like arriving on the page.
      const section = sectionRef.current
      if (section && section.getBoundingClientRect().top < 0) {
        section.scrollIntoView({
          block: 'start',
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        })
      }
    },
    [count]
  )

  /**
   * Every mounted email measures itself, keyed off its own `data-flow-key`
   * so one stable callback can serve all three slots — the observer then
   * survives a focus change instead of being torn down and re-attached.
   *
   * The frame reads `offsetHeight` rather than the entry it was handed:
   * notifications that arrive while the observer's frame is still pending
   * are dropped (and none arrive at all while the tab is hidden, where
   * `requestAnimationFrame` does not run), so the entry can be several
   * layouts behind. `offsetHeight` is also the untransformed box, which is
   * what the neighbours are laid out from — `contentRect` is not.
   */
  const measure = useCallback((element: HTMLElement | null) => {
    const key = element?.dataset.flowKey
    if (!element || !key || typeof ResizeObserver === 'undefined') {
      return
    }
    const observer = createRafResizeObserver(() => {
      const height = element.offsetHeight
      // Zero is never a real email: it is the `hidden md:block` neighbour
      // below md, or a frame caught mid-swap. Keep the last true height so
      // a stale zero can never collapse the stage.
      if (height <= 0) {
        return
      }
      setHeightByKey((current) =>
        current[key] === height ? current : { ...current, [key]: height }
      )
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault()
        goTo(focusIndex + 1)
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault()
        goTo(focusIndex - 1)
      }
    },
    [focusIndex, goTo]
  )

  if (!focus) {
    return null
  }

  const previousLabel = previous
    ? describeStepGap(tFlows, focus.dayOffset, previous.dayOffset, 'before')
    : null
  const nextLabel = next
    ? describeStepGap(tFlows, focus.dayOffset, next.dayOffset, 'later')
    : null
  const labelBySlot: Record<StageSlot, string | null> = {
    focus: null,
    previous: previousLabel,
    next: nextLabel,
  }

  /**
   * How far down the stage a neighbour reaches: dropped `SIDE_TOP_OFFSET_PX`
   * and drawn at `SIDE_SCALE`, so its own height counts for half.
   */
  const sideReach = (email: StageEmail | undefined) =>
    email ? SIDE_TOP_OFFSET_PX + (heightByKey[email.key] ?? 0) * SIDE_SCALE : 0
  const focusHeight = heightByKey[focus.key] ?? STAGE_MIN_HEIGHT_PX
  const stageHeight = Math.max(
    focusHeight,
    sideReach(previous),
    sideReach(next)
  )
  const formatCount = (value: number) => numberFormat(locale).format(value)
  const total = formatCount(count)

  return (
    <section
      ref={sectionRef}
      aria-label={t('flows.stage.sequenceLabel', { count })}
      className="relative w-full overflow-x-hidden"
    >
      <div
        className={cn('mx-auto w-full px-4 md:px-6', MOTION_CLASSES)}
        style={{ maxWidth: focus.width }}
      >
        <div className="mb-4">
          <FocusHeader email={focus} brand={brand} />
        </div>
        {count > 1 ? (
          <div className="mb-4 flex items-center justify-between gap-3 md:hidden">
            <button
              type="button"
              disabled={!previous}
              onClick={() => goTo(focusIndex - 1)}
              className="flex items-center gap-1 font-medium text-muted-foreground text-xs transition-colors hover:text-foreground disabled:invisible"
            >
              <FlowChevronLeftIcon aria-hidden className="size-3.5" />
              {previousLabel}
            </button>
            <button
              type="button"
              disabled={!next}
              onClick={() => goTo(focusIndex + 1)}
              className="flex items-center gap-1 font-medium text-muted-foreground text-xs transition-colors hover:text-foreground disabled:invisible"
            >
              {nextLabel}
              <FlowChevronRightIcon aria-hidden className="size-3.5" />
            </button>
          </div>
        ) : null}
        <div
          role="group"
          aria-roledescription={t('flows.stage.carouselRole')}
          aria-label={t('flows.stage.carouselLabel')}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: the stage takes focus so ←/→ move between emails (the WAI-ARIA carousel pattern); the side emails and links inside stay individually focusable.
          tabIndex={0}
          onKeyDown={handleKeyDown}
          className={cn(
            'relative w-full overflow-visible outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
            // The section clips what leaves this box, so it has to clear
            // whichever email reaches lowest — a short focus would otherwise
            // cut the long neighbours off at its own bottom edge. Below md
            // the neighbours are display:none, so there the focus is the
            // whole stage.
            'h-[var(--flow-stage-focus-height)] md:h-[var(--flow-stage-height)]',
            MOTION_CLASSES
          )}
          style={
            {
              '--flow-stage-focus-height': `${focusHeight}px`,
              '--flow-stage-height': `${stageHeight}px`,
            } as CSSProperties
          }
        >
          {/*
            One element per email, keyed by the step, whatever its slot: a
            click only changes the transform, so the neighbour glides into
            the centre instead of being remounted there.
          */}
          {emails.map((email, index) => {
            const offset = index - focusIndex
            if (Math.abs(offset) > 1) {
              return null
            }
            const isFocus = offset === 0
            const direction = offset < 0 ? -1 : 1
            const slot = slotFor(offset)
            const label = labelBySlot[slot]
            const position = formatCount(index + 1)
            const showLabel = label
              ? t('flows.stage.showEmailWithGap', {
                  position,
                  subject: email.subject,
                  gap: label,
                })
              : t('flows.stage.showEmail', { position, subject: email.subject })
            return (
              <article
                key={email.key}
                ref={measure}
                aria-current={isFocus ? 'true' : undefined}
                aria-label={t('flows.stage.emailPosition', {
                  position,
                  total,
                  subject: email.subject,
                })}
                data-flow-key={email.key}
                data-flow-slot={slot}
                className={cn(
                  'absolute top-0 left-0 w-full origin-top rounded-lg',
                  MOTION_CLASSES,
                  // The focus is opaque so the neighbours tucked under it never
                  // show through the frame's translucent padding.
                  isFocus
                    ? 'z-10 bg-background opacity-100'
                    : 'hidden opacity-70 saturate-75 hover:opacity-90 md:block'
                )}
                style={{
                  transform: isFocus
                    ? 'translateX(0) scale(1)'
                    : sideTransform(direction),
                }}
              >
                <div
                  aria-hidden={!isFocus}
                  className={cn(!isFocus && 'pointer-events-none')}
                >
                  <StageEmailFrame email={email} />
                </div>
                {isFocus ? null : (
                  <button
                    type="button"
                    onClick={() => goTo(index)}
                    aria-label={showLabel}
                    className="absolute inset-0 cursor-pointer rounded-lg focus-visible:ring-2 focus-visible:ring-ring/50"
                  />
                )}
              </article>
            )
          })}
          {previous && previousLabel ? (
            <SideLabel
              direction={-1}
              label={previousLabel}
              onClick={() => goTo(focusIndex - 1)}
            />
          ) : null}
          {next && nextLabel ? (
            <SideLabel
              direction={1}
              label={nextLabel}
              onClick={() => goTo(focusIndex + 1)}
            />
          ) : null}
        </div>
      </div>
    </section>
  )
}
